import { ListType } from '../constants/enums'
import type { LinkedList } from './linkedlist'

/**
 * Returns the ID of the next/previous (reverse = true) node in `list`.
 * If the current node is the last/first node in the list, the first/last
 * node is returned.
 * @param id ID of the current node, or `null` to use the first/last node in `list`
 * @param list List to traverse
 * @param reverse `True` to traverse list in reverse
 */
export function getNextOrPrevNodeID<T extends ListType>(
    id: number | null,
    list: LinkedList<T>,
    reverse?: boolean,
): number | undefined {
    if (!list || (typeof id === 'number' && !list.exists(id))) return
    return !reverse ? list.getNextID(id) : list.getPrevID(id)
}

/**
 * Returns the ID of the next/previous (reverse = true) node in `list`.
 * If the current node is the last/first node in the list, the first/last
 * node is returned. If the current ID is not in the list, the `refList`
 * is traversed until a node in `list` is found.
 * @param id ID of the current node, or `null` to use the first/last node in `list`
 * @param list List to traverse
 * @param refList List to traverse if `id` is not found in `list`
 * @param reverse `True` to traverse list in reverse
 */
export function findNextOrPrevNodeID<T extends ListType>(
    id: number | null,
    list: LinkedList<T>,
    refList: LinkedList<T>,
    reverse?: boolean,
): number {
    if (!list) return id
    let idSuccessor = getNextOrPrevNodeID(id, list, reverse)
    if (idSuccessor !== undefined) return idSuccessor
    idSuccessor = getNextOrPrevNodeID(id, refList, reverse)
    if (idSuccessor === undefined) {
        // If `id` is not in `refList`, return the first/last node in `list`
        return getNextOrPrevNodeID(null, list, reverse)
    }
    // Traverse `refList` until a node in `list` is found
    let count = 0
    while (
        idSuccessor !== undefined &&
        !list.exists(idSuccessor) &&
        count < refList.size()
    ) {
        idSuccessor = getNextOrPrevNodeID(idSuccessor, refList, reverse)
        count++
    }
    if (idSuccessor === undefined) {
        // Return the first/last node in `list`
        return getNextOrPrevNodeID(null, list, reverse)
    }
    return idSuccessor
}
